<template>
    <Transition v-if="isLoading"
                name="spinner">
        <div id="cover-spin">
            <span class="loader">
                <span>{{ $spinnerText }}</span>
            </span>
        </div>
    </Transition>

    <v-form :name="props.formProps.formName">
        <Tabs value="0"
              scrollable>

            <TabList class="mb-4">
                <Tab value="0">{{ __('Basic data') }}</Tab>
                <Tab value="1">{{ __('Changelog') }}</Tab>
            </TabList>

            <TabPanels>
                <TabPanel value="0">
                    <div class="grid">
                        <FormCalendar fieldName="activity_date"
                                      label="Activity date"
                                      size="2"
                                      validation="required"
                                      showTime
                                      hourFormat="24"
                                      :isFormDisabled="isFormDisabled"
                                      v-model:field="crud.activity_date" />

                        <FormSelect fieldName="dcttype_id"
                                    label="Type"
                                    size="6"
                                    validation="required"
                                    :showClear="true"
                                    :isFormDisabled="isFormDisabled"
                                    :options="select?.dct_activity_types"
                                    v-model:field="crud.dcttype_id" />
                        <FormSelect fieldName="creator_id"
                                    label="Creator"
                                    size="4"
                                    :isFormDisabled="true"
                                    :options="select?.users"
                                    v-model:field="crud.creator_id" />
                    </div>
                    <div class="grid">
                        <FormSelect fieldName="source_table"
                                    label="Binded area"
                                    size="2"
                                    :showClear="true"
                                    :isFormDisabled="isFormDisabled"
                                    :options="select?.activity_tables"
                                    :change="(e) => getRecordForSelectedTable(e.value)"
                                    v-model:field="crud.source_table" />

                        <FormSelect fieldName="source_id"
                                    label="Binded data"
                                    size="10"
                                    :showClear="true"
                                    :isFormDisabled="isFormDisabled"
                                    :options="select?.activity_table_records"
                                    v-model:field="crud.source_id" />
                    </div>
                    <div class="grid">

                    </div>
                    <div class="grid">
                        <FormInputText fieldName="subject"
                                       label="Subject"
                                       validation="required"
                                       size="8"
                                       v-model:field="crud.subject" />

                        <FormSelect fieldName="status_id"
                                    label="Status"
                                    size="2"
                                    validation="required"
                                    :isFormDisabled="isFormDisabled"
                                    :options="select?.statuses"
                                    v-model:field="crud.status_id" />

                        <FormRadioButton fieldName="visibility"
                                         label="Visibility"
                                         validation="required"
                                         size="2"
                                         :isFormDisabled="isFormDisabled"
                                         :data="select?.activity_visibilities"
                                         v-model:field="crud.visibility" />
                    </div>
                    <div class="grid">
                        <FormEditor fieldName="description"
                                    label="Description"
                                    :isFormDisabled="isFormDisabled"
                                    v-model:field="crud.description" />
                    </div>
                </TabPanel>

                <TabPanel v-if="crud.log"
                          value="1"
                          :pt="{ root: { class: 'form-tab-panel' } }">
                    <ChangeLog :log="crud?.log" />
                </TabPanel>
            </TabPanels>
        </Tabs>

        <div class="grid">
            <FormButtons :formProps="props.formProps"
                         :sourceForm="crud"
                         :validation="validation"
                         v-model:isFormDisabled="isFormDisabled">
                <template #after-in-show>
                    <Button v-if="crud.source_table && crud.source_id"
                            style="margin:0 5px"
                            class="bg-emerald-500 hover:bg-green-200 text-white hover:text-green-600 font-medium py-1 px-2.5 rounded-md border border-gray-200 hover:border-gray-300 transition-colors duration-150 flex items-center gap-2"
                            :label="__('Go to source')"
                            icon="pi pi-external-link"
                            @click="goToSource()" />
                </template>
                <template #after-in-edit>
                    <AlertAddButton :addAlert="addAlert" />
                </template>
            </FormButtons>
        </div>
        <AlertAdd :formProps="{ source_table: 'activities', source_id: crud.id, width: '50%' }"
                  v-model:isAddAlertDialogOpen="isAddAlertDialogOpen" />
    </v-form>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { router } from '@inertiajs/vue3';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';

import { validation, initFormObj } from '@pages/Helper/crud_utils.js'
import FormInputText from '@crud/FormInputText.vue'
import FormSelect from '@crud/FormSelect.vue'
import FormEditor from '@crud/FormEditor.vue'
import FormButtons from '@crud/FormButtons.vue'
import FormRadioButton from '@crud/FormRadioButton.vue'
import FormCalendar from '@crud/FormCalendar.vue';
import Button from 'primevue/button';
import ChangeLog from '@crud/ChangeLog.vue'
import AlertAdd from '@app/Data/Alert/AlertAdd.vue'
import AlertAddButton from '@alert/AlertAddButton.vue'
import CustomButton from '@crud/CustomButton.vue'
import { __, getFromRoute, getStoreValue, currentDateTime, singularize } from '@helper/utils.js'

// -----------------------------------------------------

const props = defineProps({
    formProps: Object,
})

const isLoading = ref(true);
const isFormDisabled = ref(true)
const crud = ref({})

const options = ["dct_activity_types", "activity_visibilities", "users", "statuses", "activity_tables", "activity_table_records"];
const select = ref(null);
const isAddAlertDialogOpen = ref(false);
const sourceRoutes = {
    'opportunities': 'opportunity-index',
};

// -----------------------------------------------------

onMounted(async () => {
    await initFormObj({ props, crud, isLoading, options, select, paramsToSelect: { 'statuses': { 'status_category': 'STA_ACTIVITY' } } })

    if (!crud.value.visibility) {
        crud.value.visibility = 'A';
    }

    if (props.formProps.addMode == true) {
        crud.value.creator_id = getStoreValue('myId');
        crud.value.activity_date = currentDateTime();
    } else {
        await getRecordForSelectedTable(crud.value.source_table)
    }
})

// -----------------------------------------------------

const addAlert = async () => {
    isAddAlertDialogOpen.value = true;
}

// -----------------------------------------------------

const getRecordForSelectedTable = async (table) => {
    const response = await getFromRoute('helper-select', { select: 'table_records', params: { 'table': table } })
    select.value.activity_table_records = response.table_records
}

// -----------------------------------------------------

// Przejdź do źródła aktywności
function goToSource() {
    let routeName = sourceRoutes[crud.value.source_table];
    if (!routeName) {
        routeName = singularize(crud.value.source_table) + '-index';
    }
    if (!routeName) {
        console.warn('Nie znaleziono route dla tabeli źródłowej:', crud.value.source_table);
        return;
    }
    if (routeName) {
        router.visit(route(routeName, { id: crud.value.source_id },
        ));
    }
};

// ------------------------------------------------------------------------------------------------------------

</script>

<style lang="scss" scoped></style>
